/* eslint-disable react/prop-types -- this codebase does not use propTypes (see CLAUDE.md). */
import { createContext, useCallback, useContext, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useStore } from "../../contexts/StoreContext";
import * as startApi from "./api";
import { pickInitForm, pickStartPass } from "./setupChain";

// Server state the /start screens share. Which step a person is on is always
// worked out from these records, never from local state, so a reload, a second
// phone or a return from Stripe lands them where they really are.

const StartContext = createContext(null);

export function StartDataProvider({ children }) {
  const { selectedStore } = useStore();
  const storeId = selectedStore?.id;
  const queryClient = useQueryClient();
  const enabled = !!storeId;

  const statusQuery = useQuery({
    // Same key as useSetupStatus, so the dashboard sees what /start learned.
    queryKey: ["setup-status", storeId],
    queryFn: () => startApi.checkSetup(storeId),
    enabled,
    retry: 2,
  });
  const passesQuery = useQuery({
    queryKey: ["start", "passes", storeId],
    queryFn: () => startApi.listPasses(storeId),
    enabled,
    retry: 2,
  });
  const formsQuery = useQuery({
    queryKey: ["start", "forms", storeId],
    queryFn: () => startApi.listForms(storeId),
    enabled,
    retry: 2,
  });
  const flyersQuery = useQuery({
    queryKey: ["start", "flyers", storeId],
    queryFn: () => startApi.listFlyers(storeId),
    enabled,
    retry: 2,
  });

  const startPass = pickStartPass(passesQuery.data || []);
  const initForm = startPass ? pickInitForm(formsQuery.data || [], startPass.id) : null;
  const flyers = useMemo(() => flyersQuery.data || [], [flyersQuery.data]);
  const flyer =
    (initForm && flyers.find((f) => String(f.form?.id) === String(initForm.id))) || flyers[0] || null;

  const refresh = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["stores"] }),
      queryClient.invalidateQueries({ queryKey: ["setup-status", storeId] }),
      queryClient.invalidateQueries({ queryKey: ["start"] }),
      queryClient.invalidateQueries({ queryKey: ["passes", storeId] }),
      queryClient.invalidateQueries({ queryKey: ["forms", storeId] }),
      queryClient.invalidateQueries({ queryKey: ["flyers", storeId] }),
    ]);
  }, [queryClient, storeId]);

  const isLoading =
    enabled &&
    (statusQuery.isLoading || passesQuery.isLoading || formsQuery.isLoading || flyersQuery.isLoading);
  const error = statusQuery.error || passesQuery.error || formsQuery.error || flyersQuery.error;

  const value = useMemo(
    () => ({
      store: selectedStore || null,
      storeId,
      status: statusQuery.data || null,
      passes: passesQuery.data || [],
      startPass,
      forms: formsQuery.data || [],
      initForm,
      flyers,
      flyer,
      isLoading,
      error,
      refresh,
      refetchStatus: statusQuery.refetch,
    }),
    [
      selectedStore,
      storeId,
      statusQuery.data,
      statusQuery.refetch,
      passesQuery.data,
      startPass,
      formsQuery.data,
      initForm,
      flyers,
      flyer,
      isLoading,
      error,
      refresh,
    ],
  );

  return <StartContext.Provider value={value}>{children}</StartContext.Provider>;
}

export function useStartData() {
  const value = useContext(StartContext);
  if (!value) throw new Error("useStartData must be used inside StartDataProvider");
  return value;
}
